
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';

interface TableOfContentsItem {
  id: string;
  title: string;
  onClick: () => void;
}

interface TableOfContentsProps {
  items: TableOfContentsItem[];
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ items }) => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(true);
  
  return (
    <nav 
      aria-labelledby="toc-heading" 
      className="glass-card p-4 md:p-6 rounded-lg mb-6 md:mb-8"
      role="navigation"
    >
      <h2 
        id="toc-heading" 
        className="text-lg md:text-xl font-future tracking-wide font-semibold mb-3 md:mb-4 flex items-center justify-between text-gray-200"
      >
        <span>{t.tableOfContents}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-gray-400 hover:text-purple-300 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-controls="toc-list"
          aria-label={isExpanded ? t.collapseTOC : t.expandTOC}
        >
          {isExpanded ? 
            <ChevronUp className="h-5 w-5" aria-hidden="true" /> : 
            <ChevronDown className="h-5 w-5" aria-hidden="true" />
          }
        </Button>
      </h2>
      {isExpanded && (
        <ol 
          id="toc-list"
          className={`space-y-2 list-decimal list-inside text-gray-200 ${isMobile ? 'text-sm' : ''}`}
          role="list"
        >
          {items.map((item, index) => (
            <li key={`toc-item-${index}`}>
              <Button 
                variant="ghost"
                size="sm"
                onClick={item.onClick}
                className="text-left justify-start hover:text-purple-400 hover:bg-purple-500/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded px-2 w-full text-gray-300"
                aria-label={`${t.navigateToSection}: ${item.title}`}
              >
                <span className="line-clamp-1">{item.title}</span>
              </Button>
            </li>
          ))}
        </ol>
      )}
    </nav>
  );
};

export default TableOfContents;
